import { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const ProfileSection = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    businessName: "",
    website: "",
    bio: ""
  });
  const [errors, setErrors] = useState({});
  const [avatar, setAvatar] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  // Load profile data on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

        if (error) throw error;

        if (data) {
          setFormData({
            fullName: data?.full_name || "",
            email: data?.email || user?.email || "",
            businessName: data?.business_name || "",
            website: data?.website || "",
            bio: data?.bio || ""
          });
          setAvatar(data?.avatar_url || "");
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setErrors({ load: error?.message || 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSaveMessage("");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.fullName?.trim()) newErrors.fullName = "Full name is required";
    if (!formData?.email?.trim()) newErrors.email = "Email is required";
    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = "Invalid email format";
    }
    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      newErrors.website = "Invalid URL format";
    }
    if (formData?.bio && formData?.bio?.length > 500) {
      newErrors.bio = "Bio must be 500 characters or less";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!user?.id) {
      setErrors({ save: 'User not authenticated' });
      return;
    }

    try {
      setSaving(true);
      setSaveMessage("");
      setErrors({});

      const { error } = await supabase?.from('user_profiles')?.update({
          full_name: formData?.fullName?.trim(),
          email: formData?.email?.trim(),
          business_name: formData?.businessName?.trim() || null,
          website: formData?.website?.trim() || null,
          bio: formData?.bio?.trim() || null,
          avatar_url: avatar || null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (error) throw error;

      setSaveMessage("Profile updated successfully!");
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ save: error?.message || 'Failed to save profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    setSaveMessage("");
    // Reload original data
    if (user?.id) {
      supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single()?.then(({ data }) => {
          if (data) {
            setFormData({
              fullName: data?.full_name || "",
              email: data?.email || user?.email || "",
              businessName: data?.business_name || "",
              website: data?.website || "",
              bio: data?.bio || ""
            });
            setAvatar(data?.avatar_url || "");
          }
        });
    }
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Profile Information</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your personal and business details</p>
        </div>
        {!isEditing && (
          <Button variant="outline" iconName="Edit2" iconPosition="left" onClick={() => setIsEditing(true)}>
            Edit Profile
          </Button>
        )}
      </div>
      {/* Success Message */}
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          {saveMessage}
        </div>
      )}
      {/* Error Messages */}
      {errors?.save && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {errors?.save}
        </div>
      )}
      {errors?.load && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          {errors?.load}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-border">
              <Image 
                src={avatar} 
                alt="Professional headshot of user with warm smile in business attire against neutral background"
                className="w-full h-full object-cover"
              />
            </div>
            {isEditing && (
              <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                <Icon name="Camera" size={20} color="#FFFFFF" />
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleAvatarChange}
                />
              </label>
            )}
          </div>
          {isEditing && (
            <p className="text-xs text-muted-foreground text-center">
              Click camera icon to upload new photo
            </p>
          )}
        </div>

        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              name="fullName"
              value={formData?.fullName}
              onChange={handleInputChange}
              disabled={!isEditing}
              required
              error={errors?.fullName}
              placeholder="Enter your full name"
            />
            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              disabled={!isEditing}
              required
              error={errors?.email}
              placeholder="Enter your email address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Business Name"
              type="text"
              name="businessName"
              value={formData?.businessName}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder="Your business name"
            />
            <Input
              label="Website"
              type="url"
              name="website"
              value={formData?.website}
              onChange={handleInputChange}
              disabled={!isEditing}
              error={errors?.website}
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData?.bio}
              onChange={handleInputChange}
              disabled={!isEditing}
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              placeholder="Tell us about yourself and your business..."
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-muted-foreground">
                {formData?.bio?.length || 0}/500 characters
              </p>
              {errors?.bio && (
                <p className="text-xs text-red-600">{errors?.bio}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button 
                variant="default" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;